import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { openWhatsApp } from '../lib/wa';

// products cycled inside the rotating bowl, each with a macro close-up + spec
const products = [
  { name: 'Basmati 1121', desc: 'Long-grain aromatic', spec: '8.30 mm + grain length', img: '/rice-bowl.png', macro: '/rice-grain.png' },
  { name: 'Sella & Steam', desc: 'Parboiled export grade', spec: 'Firm, separate grains', img: '/bowl-sella.jpg', macro: '/grain-sella.png' },
  { name: 'Himalayan Pink Salt', desc: 'Food & industrial grade', spec: 'Natural rose crystal', img: '/bowl-pinksalt.jpg', macro: '/grain-pinksalt.png' },
  { name: 'Whole Grains', desc: 'Wheat · Maize · Millet', spec: 'Sortex-cleaned', img: '/bowl-grains.jpg', macro: '/grain-grains.png' },
];

const HeroEditorial = () => {
  const [reduced, setReduced] = useState(false);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % products.length), 3200);
    return () => clearInterval(id);
  }, [paused]);

  const p = products[active];
  const pauseOn = { onMouseEnter: () => setPaused(true), onMouseLeave: () => setPaused(false) };

  return (
    <section data-testid="hero-editorial" className="relative bg-[#0A1E13] text-ktc-ivory min-h-screen overflow-hidden">
      <div className="absolute inset-0 grain-texture opacity-[0.14]" aria-hidden />
      <div className="absolute inset-0" aria-hidden style={{ background: 'radial-gradient(55% 50% at 50% 48%, rgba(201,162,94,0.14) 0%, transparent 62%)' }} />

      {/* keyline */}
      <div className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between border-b border-ktc-ivory/15 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.26em] text-ktc-ivory/55">
          <span>Rice Millers · Processors · Exporters</span>
          <span className="hidden sm:inline">Est. 1999 · Karachi, Pakistan</span>
        </div>
      </div>

      <div className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24 pb-12">
        <motion.span initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] uppercase tracking-[0.22em] text-ktc-goldLight/90">
          <span className="h-px w-8 bg-ktc-gold/60" /> Kassam Trading Company
        </motion.span>
        <motion.h1 initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.08 }} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.06] mt-4 max-w-3xl text-balance">
          Premium rice, salt &amp; grains,<br className="hidden sm:block" /> milled and exported from Pakistan.
        </motion.h1>

        {/* bowl + closer-look detail */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          {/* rotating bowl cycling products */}
          <div className="relative w-[210px] h-[210px] sm:w-[248px] sm:h-[248px]" {...pauseOn}>
            <div className="absolute inset-[1%] rounded-full border border-ktc-gold/35" />
            <motion.div className="absolute inset-[5%] rounded-full overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-ktc-ivory/10" animate={reduced ? {} : { rotate: 360 }} transition={{ duration: 80, ease: 'linear', repeat: Infinity }}>
              <AnimatePresence>
                <motion.img key={p.img} src={p.img} alt={p.name} draggable={false} className="absolute inset-0 w-full h-full object-cover scale-[1.12]" initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} />
              </AnimatePresence>
            </motion.div>
          </div>

          {/* closer-look macro detail */}
          <div className="w-[230px] flex flex-col items-center md:items-start text-center md:text-left" {...pauseOn}>
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-ktc-ivory/45">
              <span className="h-px w-5 bg-ktc-gold/50" /> A closer look
            </span>
            <div className="relative w-full h-20 sm:h-24 mt-4">
              <AnimatePresence>
                <motion.img key={p.macro} src={p.macro} alt={`${p.name} close up`} draggable={false} className="absolute inset-0 w-full h-full object-contain md:object-left drop-shadow-[0_14px_36px_rgba(201,162,94,0.4)]" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.6 }} />
              </AnimatePresence>
            </div>
            <div className="relative w-full h-[86px] mt-4">
              <AnimatePresence>
                <motion.div key={p.name} className="absolute inset-0 flex flex-col items-center md:items-start" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.5 }}>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-ktc-goldLight font-semibold">{p.name}</span>
                  <span className="font-serif text-lg sm:text-xl text-ktc-ivory leading-tight mt-0.5">{p.desc}</span>
                  <span className="text-[12px] text-ktc-ivory/55 mt-2 pt-2 border-t border-ktc-ivory/12 w-full md:w-auto md:pr-6">{p.spec}</span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* interactive product switcher */}
        <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-md">
          {products.map((it, i) => (
            <button
              key={it.name}
              type="button"
              onClick={() => setActive(i)}
              {...pauseOn}
              data-testid={`hero-product-tab-${i}`}
              className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border transition-colors ${i === active ? 'border-ktc-gold text-ktc-goldLight bg-ktc-gold/10' : 'border-ktc-ivory/15 text-ktc-ivory/55 hover:text-ktc-ivory'}`}
            >
              {it.name}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mt-7 justify-center">
          <Link to="/quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 bg-ktc-goldLight hover:bg-ktc-gold text-ktc-greenDark font-semibold px-6 py-3 rounded-md transition-colors">Request a Quote <ArrowRight size={18} /></Link>
          <Link to="/products" data-testid="hero-products-btn" className="inline-flex items-center gap-2 border border-ktc-ivory/25 hover:border-ktc-ivory text-ktc-ivory font-medium px-6 py-3 rounded-md transition-colors">Explore Products</Link>
          <button type="button" onClick={() => openWhatsApp()} data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 text-ktc-goldLight font-medium px-3 py-3 hover:gap-3 transition-all"><MessageCircle size={18} /> WhatsApp</button>
        </div>
      </div>
    </section>
  );
};

export default HeroEditorial;
